import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, ShoppingCart, GraduationCap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ProfileMenu, { MainMenuActions, AccountSelectionView } from './ProfileMenu';
import { getRoleBadgeClasses, getRoleLabel, getGuestExpirationDate } from '@/utils/roleUtils';
import { menuConfig, subMenuData, MenuItem, SubMenuItem, SubMenuCategory } from '@/config/menuConfig';
import { Button, buttonVariants } from '@/components/shared/ui/Button';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/constants/routes';
import type { TFunction } from 'i18next';
import LanguageSwitcher from '@/components/shared/LanguageSwitcher';

// Logo is now in public folder
const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

/** localStorage 'cart'에서 담긴 상품 수(quantity 합계) 반환. 항목이 문자열이면 1, { id, quantity }면 quantity 합산 */
function getCartCount(): number {
  try {
    const raw = localStorage.getItem('cart');
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return 0;
    return parsed.reduce((sum: number, x: unknown) => {
      if (x != null && typeof x === 'object' && 'quantity' in x) return sum + (Number((x as { quantity: number }).quantity) || 1);
      return sum + 1;
    }, 0);
  } catch {
    return 0;
  }
}

// 데스크톱 서브메뉴 아이템 렌더링
const renderDesktopSubMenuItems = (menuKey: string, data: SubMenuCategory, userRole: string, organizationId: string | null | undefined, t: TFunction) => {
  const renderItem = (key: string, item: SubMenuItem) => {
    const isAnchor = item.to && item.to.startsWith('#');
    let itemPath = item.to;

    const CardContent = () => (
      <div className="flex flex-col">
        <div className="flex flex-col">
          <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{t(`menu:${item.titleKey}`)}</h3>
          {item.descriptionKey && (
            <p className="text-xs text-gray-500 mt-1">{t(`menu:${item.descriptionKey}`)}</p>
          )}
        </div>
      </div>
    );

    const baseClasses = "block p-3 rounded-lg hover:bg-gray-50 transition-colors group";

    if (isAnchor) {
      return (
        <a href={itemPath} key={key} className={baseClasses}>
          <CardContent />
        </a>
      );
    }
    return (
      <Link to={itemPath || '#'} key={key} className={baseClasses}>
        <CardContent />
      </Link>
    );
  };

  return (
    <div className="flex flex-col gap-1 p-2 w-64">
      {Object.keys(data).map((key) => renderItem(key, data[key]))}
    </div>
  );
};

interface DesktopMenuItemProps {
  item: MenuItem;
  hoveredMenus: Record<string, boolean>;
  setHoveredMenus: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  userRole: string;
  organizationId?: string | null;
  t: TFunction;
}

// 데스크톱 메뉴 아이템 컴포넌트
const DesktopMenuItem: React.FC<DesktopMenuItemProps> = ({ item, hoveredMenus, setHoveredMenus, userRole, organizationId, t }) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  if (item.hasSubmenu) {
    const menuData = subMenuData[item.key];
    const isHovered = hoveredMenus[item.key] || false;

    return (
      <div
        className="relative"
        onMouseEnter={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setHoveredMenus(prev => {
            const newState: Record<string, boolean> = {};
            Object.keys(subMenuData).forEach(key => {
              newState[key] = false;
            });
            newState[item.key] = true;
            return newState;
          });
        }}
        onMouseLeave={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            setHoveredMenus(prev => ({
              ...prev,
              [item.key]: false
            }));
          }, 100);
        }}
      >
        <div
          className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors bg-transparent border-none cursor-default inline-flex items-center"
        >
          {t(`menu:${item.labelKey}`)}
          <ChevronDown
            size={16}
            className={`ms-1 transition-transform duration-200 ${isHovered ? 'rotate-180' : 'rotate-0'}`}
          />
        </div>
        <div
          className={`absolute top-full start-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 transition-all duration-200 ${isHovered ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
            }`}
          onMouseEnter={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setHoveredMenus(prev => ({
              ...prev,
              [item.key]: true
            }));
          }}
          onMouseLeave={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
              setHoveredMenus(prev => ({
                ...prev,
                [item.key]: false
              }));
            }, 100);
          }}
        >
          {renderDesktopSubMenuItems(item.key, menuData, userRole, organizationId, t)}
        </div>
      </div>
    );
  }

  /**
   * 1뎁스 GNB: 현재 경로만 브랜드 파랑, 그 외는 `slate` 톤(2번 캡처·관리자 탭처럼 ‘차분한 청회색’).
   */
  const gnbTextNavClass = ({ isActive }: { isActive: boolean }): string =>
    cn(
      'px-3 py-2 text-sm font-medium transition-colors bg-transparent border-none cursor-pointer inline-flex items-center rounded no-underline',
      isActive
        ? '!text-brand-600 visited:!text-brand-600'
        : '!text-slate-600 visited:!text-slate-600 [@media(hover:hover)]:hover:!text-brand-600',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
    );

  if (item.to) {
    return (
      <NavLink to={item.to} className={gnbTextNavClass}>
        {t(`menu:${item.labelKey}`)}
      </NavLink>
    );
  }

  return (
    <a
      href={item.href}
      className="px-3 py-2 text-sm font-medium no-underline !text-slate-600 visited:!text-slate-600 [@media(hover:hover)]:hover:!text-brand-600 transition-colors bg-transparent border-none inline-flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
    >
      {t(`menu:${item.labelKey}`)}
    </a>
  );
};

interface MobileMenuItemProps {
  item: MenuItem;
  openSubMenus: Record<string, boolean>;
  toggleSubMenu: (key: string) => void;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  closeAllSubMenus: () => void;
  userRole: string;
  organizationId?: string | null;
  t: TFunction;
}

// 모바일 메뉴 아이템 컴포넌트
const MobileMenuItem: React.FC<MobileMenuItemProps> = ({ item, openSubMenus, toggleSubMenu, setIsMobileMenuOpen, closeAllSubMenus, userRole, organizationId, t }) => {
  const isOpen = openSubMenus[item.key] || false;

  const renderSubMenuItems = (menuKey: string, data: SubMenuCategory) => {
    const renderGenericItems = (items: string[]) => items.map((key) => {
      const subItem = data[key];
      const isAnchor = subItem.to && subItem.to.startsWith('#');
      let itemPath = subItem.to;

      const props = {
        key,
        className: "block px-6 py-3 text-base text-gray-600 hover:bg-gray-50",
        onClick: () => {
          setIsMobileMenuOpen(false);
          closeAllSubMenus();
        }
      };

      if (isAnchor) {
        return (
          <a href={itemPath} {...props}>
            {t(`menu:${subItem.titleKey}`)}
          </a>
        );
      }
      return (
        <Link to={itemPath || '#'} {...props}>
          {t(`menu:${subItem.titleKey}`)}
        </Link>
      );
    });

    if (menuKey === 'scenarios') {
      const filteredData = userRole === 'guest'
        ? Object.keys(data).filter(key => key !== 'records')
        : Object.keys(data);
      return renderGenericItems(filteredData);
    }

    // Default handler for all other submenus (organization, support, adminMode, etc.)
    return renderGenericItems(Object.keys(data));
  };

  const menuItemClasses = 'w-full flex items-center justify-between px-6 py-4 text-base font-medium bg-transparent';

  if (item.hasSubmenu) {
    const menuData = subMenuData[item.key];
    return (
      <div>
        <button
          className={menuItemClasses}
          onClick={() => toggleSubMenu(item.key)}
        >
          <span>{t(`menu:${item.labelKey}`)}</span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          />
        </button>
        {isOpen && (
          <div className="bg-gray-50 py-2">
            {renderSubMenuItems(item.key, menuData)}
          </div>
        )}
      </div>
    );
  }

  if (item.to) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          cn(
            menuItemClasses,
            isActive
              ? '!text-brand-600 visited:!text-brand-600'
              : '!text-slate-600 visited:!text-slate-600 [@media(hover:hover)]:hover:!text-brand-600',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400/50 no-underline',
          )
        }
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <span>{t(`menu:${item.labelKey}`)}</span>
      </NavLink>
    );
  }

  return (
    <a
      href={item.href}
      className={menuItemClasses}
      onClick={() => setIsMobileMenuOpen(false)}
    >
      <span>{t(`menu:${item.labelKey}`)}</span>
    </a>
  );
};

export default function Gnb(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['menu', 'auth']);
  // @ts-ignore
  const { getCurrentRole, user, logout, switchAccount } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});
  const [hoveredMenus, setHoveredMenus] = useState<Record<string, boolean>>({});
  const [isAccountAccordionOpen, setIsAccountAccordionOpen] = useState<boolean>(false);
  const [isProfileActionsOpen, setIsProfileActionsOpen] = useState<boolean>(false);
  const [cartCount, setCartCount] = useState<number>(() => getCartCount());

  const userRole: string = getCurrentRole();
  const isLoggedIn = user !== null;
  const menuItems: MenuItem[] = menuConfig[userRole] || menuConfig.guest;

  const currentAccount = user?.currentAccount;
  const accounts: any[] = user?.accounts || [];
  const isGuest = userRole === 'guest';
  const currentOrganizationLabel = currentAccount?.organizationName || (
    currentAccount?.role === 'admin'
      ? t('auth:newbaseOrganization', { defaultValue: '뉴베이스' })
      : t('auth:personalUser', { defaultValue: '개인 사용자' })
  );

  // Helpers moved to utils/roleUtils.js

  const handleAccountSwitch = (accountId: string | number) => {
    switchAccount(String(accountId));
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleAddAccount = () => {
    setIsMobileMenuOpen(false);
    navigate(ROUTES.AUTH.ADD_ACCOUNT);
  };

  const handleEmailVerification = () => {
    setIsMobileMenuOpen(false);
    navigate('/verify-email');
  };

  const toggleSubMenu = (menuKey: string) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const closeAllSubMenus = () => {
    setOpenSubMenus({});
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    closeAllSubMenus();
  }, [location.pathname]);

  useEffect(() => {
    setCartCount(getCartCount());
    const handler = () => setCartCount(getCartCount());
    window.addEventListener('cart-updated', handler);
    return () => window.removeEventListener('cart-updated', handler);
  }, []);

  // 모바일 메뉴 열림/닫힘 시 body 스크롤 제어
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsAccountAccordionOpen(false);
      setIsProfileActionsOpen(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const containerMaxWidth = 'max-w-[1460px]';

  return (
    <>
      <nav
        data-gnb
        className="fixed inset-x-0 top-0 h-16 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-[998] transition-all"
      >
        <div className={`mx-auto px-4 h-full flex items-center justify-between relative z-[998] ${containerMaxWidth}`}>
          <div className="flex items-center gap-12">
            <Link to="/" className="flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
              <img src={medicrewLogo} alt="Medicrew" className="h-6 w-auto" />
            </Link>
            <div className="hidden lg:flex items-center gap-1">
              {menuItems.map((item) => (
                <DesktopMenuItem
                  key={item.key}
                  item={item}
                  hoveredMenus={hoveredMenus}
                  setHoveredMenus={setHoveredMenus}
                  userRole={userRole}
                  organizationId={currentAccount?.organizationId}
                  t={t}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2">
              {isLoggedIn && userRole !== 'admin' && (
                <Link to={userRole === 'master' ? '/master/my-classes' : '/student/my-classes'}>
                  <Button variant="primary" size="md">
                    {t('menu:myClass')}
                  </Button>
                </Link>
              )}
              {isLoggedIn && userRole === 'master' && (
                <Link to={ROUTES.MASTER.ORGANIZATION}>
                  <Button variant="outline" size="md">
                    {t('menu:admin')}
                  </Button>
                </Link>
              )}
              {isLoggedIn && userRole === 'admin' && (
                <Link to={ROUTES.ADMIN.ORGANIZATIONS}>
                  <Button variant="outline" size="md">
                    {t('menu:admin')}
                  </Button>
                </Link>
              )}
                {isLoggedIn && (userRole === 'student' || userRole === 'master') && (
                <button
                  type="button"
                  className={cn(
                    buttonVariants({ variant: 'ghost', size: 'md' }),
                    'px-3 text-gray-800 hover:text-brand-700 hover:bg-slate-100',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/70 focus-visible:ring-offset-2'
                  )}
                  onClick={() =>
                    navigate(
                      userRole === 'master' ? ROUTES.MASTER.ORDERS : '/student/orders'
                    )
                  }
                >
                  {t('menu:orderInquiry')}
                </button>
              )}
              {/* 장바구니 아이콘 (admin 제외, 로그인 전에도 이용 가능) */}
              {userRole !== 'admin' && (
                <Link to={isLoggedIn && userRole === 'master' ? '/master/cart' : ROUTES.COMMON.CART}>
                  <Button variant="ghost" size="md" className="relative" aria-label={t('menu:cart')}>
                    <ShoppingCart size={20} />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -end-1 bg-brand-500 text-white text-xs font-medium rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center">
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}
              {isLoggedIn ? (
                <ProfileMenu />
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="md">
                      {t('menu:login')}
                    </Button>
                  </Link>
                  <Link to={ROUTES.COMMON.CONTACT}>
                    <Button variant="primary" size="md">
                      {t('menu:demoRequest')}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            <button
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 -me-2"
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* 모바일 메뉴 (nav 외부) */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 top-16 bg-black/50 z-[997] lg:hidden animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed inset-x-0 top-16 bottom-0 bg-white z-[998] lg:hidden overflow-y-auto animate-in slide-in-from-top-10 duration-200">

            {/* ========== 모바일 프로필 메뉴 섹션 시작 ========== */}
            {/* 프로필 섹션 (로그인한 사용자만) */}
            {isLoggedIn && user && (
              <div>
                <button
                  onClick={() => setIsProfileActionsOpen(!isProfileActionsOpen)}
                  className="w-full hover:bg-gray-50 transition-colors"
                >
                  <div className="px-6 py-6">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-bold text-gray-900 text-start truncate mb-1">{user.name}</h4>
                        <span className="text-sm font-medium text-gray-600 text-start block truncate">
                          {currentOrganizationLabel}
                        </span>
                      </div>
                      <span
                        className={`px-2.5 py-0.5 rounded text-xs font-bold border whitespace-nowrap flex-shrink-0 ${getRoleBadgeClasses(currentAccount?.role || '')}`}
                      >
                        {getRoleLabel(currentAccount?.role || '')}
                      </span>
                      <ChevronDown
                        size={20}
                        className={`text-gray-500 transition-transform duration-200 flex-shrink-0 ${isProfileActionsOpen ? 'rotate-180' : 'rotate-0'}`}
                      />
                    </div>
                  </div>
                </button>

                {isProfileActionsOpen && (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    <div>
                      {isAccountAccordionOpen ? (
                        <AccountSelectionView
                          accounts={accounts.filter((acc) => acc.accountId !== currentAccount?.accountId)}
                          currentAccount={currentAccount as any}
                          handleAccountSwitch={handleAccountSwitch}
                          onBack={() => setIsAccountAccordionOpen(false)}
                          getRoleBadgeClasses={getRoleBadgeClasses}
                          getRoleLabel={getRoleLabel}
                          showHeader={true}
                          onAddAccount={handleAddAccount}
                        />
                      ) : (
                        <MainMenuActions
                          user={user as any}
                          isGuest={isGuest}
                          handleEmailVerification={handleEmailVerification}
                          getGuestExpirationDate={getGuestExpirationDate}
                          handleAddAccount={handleAddAccount}
                          handleAccountSwitch={handleAccountSwitch}
                          navigate={navigate}
                          handleLogout={handleLogout}
                          getRoleBadgeClasses={getRoleBadgeClasses}
                          getRoleLabel={getRoleLabel}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* ========== 모바일 프로필 메뉴 섹션 끝 ========== */}

            {/* 액션 버튼들 */}
            {isLoggedIn && (
              <div className="grid grid-cols-2 gap-2 px-6 py-1">
                {isLoggedIn && userRole !== 'admin' && (
                  <Link
                    to={userRole === 'master' ? '/master/my-classes' : '/student/my-classes'}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
                  >
                    <GraduationCap size={18} className="text-white" />
                    <span>{t('menu:myClass')}</span>
                  </Link>
                )}
                {isLoggedIn && userRole === 'master' && (
                  <Link
                    to={ROUTES.MASTER.ORGANIZATION}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-brand-600 border border-brand-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <span>{t('menu:admin')}</span>
                  </Link>
                )}
                {isLoggedIn && userRole === 'admin' && (
                  <Link
                    to={ROUTES.ADMIN.ORGANIZATIONS}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-brand-600 border border-brand-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <span>{t('menu:admin')}</span>
                  </Link>
                )}
                {isLoggedIn && (userRole === 'student' || userRole === 'master') && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(
                        userRole === 'master' ? ROUTES.MASTER.ORDERS : '/student/orders'
                      );
                    }}
                    className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-brand-600 border border-brand-600 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <span>{t('menu:orderInquiry')}</span>
                  </button>
                )}
                {userRole !== 'admin' && (
                  <Link
                    to={isLoggedIn && userRole === 'master' ? '/master/cart' : ROUTES.COMMON.CART}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-brand-600 border border-brand-600 rounded-lg hover:bg-blue-50 transition-colors relative"
                  >
                    <ShoppingCart size={18} className="text-brand-600" />
                    <span>{t('menu:cart')}</span>
                    {cartCount > 0 && (
                      <span className="absolute top-1.5 end-4 bg-brand-500 text-white text-xs font-medium rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center">
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            )}

            {/* 메인 메뉴 항목들 */}
            <div className="py-2">
              {menuItems.map((item) => (
                <MobileMenuItem
                  key={item.key}
                  item={item}
                  openSubMenus={openSubMenus}
                  toggleSubMenu={toggleSubMenu}
                  organizationId={currentAccount?.organizationId}
                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                  closeAllSubMenus={closeAllSubMenus}
                  userRole={userRole}
                  t={t}
                />
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex justify-center">
                <LanguageSwitcher variant="labeled" contentClassName="min-w-[12rem]" />
              </div>
            </div>

            {/* 비로그인 사용자 버튼 */}
            {!isLoggedIn && (
              <div className="px-6 py-4 flex flex-col gap-3">
                <Link to={ROUTES.COMMON.CART} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-brand-600 border border-brand-600 rounded-lg hover:bg-blue-50 transition-colors relative">
                  <ShoppingCart size={18} className="text-brand-600" />
                  <span>{t('menu:cart')}</span>
                  {cartCount > 0 && (
                    <span className="absolute top-1.5 end-4 bg-brand-500 text-white text-xs font-medium rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Link>
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="ghost" size="md" className="w-full">
                    {t('menu:login')}
                  </Button>
                </Link>
                <Link to={ROUTES.COMMON.CONTACT} onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="primary" size="md" className="w-full">
                    {t('menu:demoRequest')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
